import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Search, Download } from "lucide-react";
import { Link } from 'react-router-dom';
import Loading from "../../component/Loading/Loading";
import AlertMsg from '../../component/AlertMsg/AlertMsg';
import { useSelector } from 'react-redux';
import { Trim } from '../../utils/Trim/Trim';
import Pagination from '../../utils/Pagenation/Pagenation';
import useAxiosInstance from '../../services/useAxiosInstance';
import GlassmorphicModal from "../../component/ReusableComponents/GlassmorphicModal";

const Newpublishing = () => {
  const axiosInstance = useAxiosInstance();
  const [data, setData] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newsIdToDelete, setNewsIdToDelete] = useState(null);
  const [currentNewsItem, setCurrentNewsItem] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const communityId = useSelector((state) => state.auth.communityId);
  const userId = useSelector((state) => state.auth.userId);
  const [alert, setAlert] = useState({ visible: false, message: '', type: '' });

  useEffect(() => {
    fetchNews();
  }, [userId, communityId]);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/public/news/byCommunity/${communityId}`);
      if (Array.isArray(response.data)) {
        setData(response.data);
      } else {
        console.error('API response is not an array:', response.data);
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const pageCount = Math.ceil(data.length / itemsPerPage);
  const filteredData = Array.isArray(data) ? data.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];
  const currentPageData = filteredData.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const handleDownloadClick = () => {
    const isAnyCheckboxChecked = Object.values(checkedItems).some(Boolean);
    if (isAnyCheckboxChecked) {
      console.log('Download button clicked');
    }
  };

  const handleCreateNewsClick = () => {
    setCheckedItems({});
  };

  const openDeleteModal = (newsid) => {
    setNewsIdToDelete(newsid);
    setShowDeleteModal(true);
  };

  const closeModal = () => {
    setShowDeleteModal(false);
    setShowEditModal(false);
    setNewsIdToDelete(null);
    setCurrentNewsItem(null);
    setFormErrors({});
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await axiosInstance.delete(`/api/public/news/delete/${newsIdToDelete}`);
      setData(data.filter(item => item.newsid !== newsIdToDelete));
      setAlert({ visible: true, message: 'News deleted successfully!', type: 'success' });
      closeModal();
    } catch (error) {
      console.error('Error deleting news item:', error);
      setAlert({ visible: true, message: 'Error deleting news: Please try again', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (newsItem) => {
    setCurrentNewsItem({ ...newsItem });
    setShowEditModal(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!currentNewsItem.title) errors.title = 'Title is required';
    if (!currentNewsItem.newscontent) errors.newscontent = 'Content is required';
    if (!currentNewsItem.datepublished) errors.datepublished = 'Date Published is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateNews = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await axiosInstance.put(`/api/public/news/update/${currentNewsItem.newsid}`, currentNewsItem);
      setData(data.map(item => (item.newsid === currentNewsItem.newsid ? currentNewsItem : item)));
      setAlert({ visible: true, message: 'News updated successfully!', type: 'success' });
      closeModal();
    } catch (error) {
      console.error('Error updating news item:', error);
      setAlert({ visible: true, message: 'Error updating news: Please try again', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Edit modal footer with action buttons
  const editModalFooter = (
    <div className="flex justify-end space-x-2">
      <button
        type="button"
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        onClick={closeModal}
      >
        Cancel
      </button>
      <button
        type="button"
        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        onClick={handleUpdateNews}
      >
        Update News
      </button>
    </div>
  );

  // Delete modal footer with action buttons
  const deleteModalFooter = (
    <div className="flex justify-end space-x-2">
      <button
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        onClick={closeModal}
      >
        Cancel
      </button>
      <button
        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
        onClick={confirmDelete}
      >
        Delete
      </button>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen md:p-4">
      {loading && <Loading />}
      {!loading && (
        <div className="max-w-7xl mx-auto">
          {alert.visible && (
            <AlertMsg
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert({ ...alert, visible: false })}
            />
          )}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">News Publishing</h1>
            <div className="flex items-center gap-2">
              {Object.values(checkedItems).some(Boolean) && (
                <button
                  className="bg-white border border-blue-500 text-blue-500 hover:bg-blue-50 px-4 py-2 rounded-md flex items-center gap-2"
                  onClick={handleDownloadClick}
                >
                  <Download size={18} />
                  <span>Download</span>
                </button>
              )}
              <Link
                to="/create-new-publishing"
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2"
                onClick={handleCreateNewsClick}
              >
                <Plus size={18} />
                <span>Create News</span>
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div className="flex flex-wrap md:flex-nowrap justify-between items-center p-4">
              <div className="relative w-full md:w-80 mb-4 md:mb-0">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search by title..."
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-400">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Title</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">News Content</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Date Published</th>
                    <th className="px-6 py-3 text-center text-sm font-medium text-white tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentPageData.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">No Data Found</td>
                    </tr>
                  ) : (
                    currentPageData.map((item) => (
                      <tr key={item.newsid} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.title}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{Trim(item.newscontent)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(item.datepublished).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex justify-center gap-3">
                          <button
                            className="text-gray-500 hover:text-gray-700"
                            onClick={() => openEditModal(item)}
                            title="Edit News"
                          >
                            <Pencil size={18} />
                          </button>
                          <button
                            className="text-red-500 hover:text-red-700"
                            onClick={() => openDeleteModal(item.newsid)}
                            title="Delete News"
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Page {currentPage + 1} of {pageCount}
              </div>
              <div className="flex-1 flex justify-end">
                <Pagination
                  currentPage={currentPage}
                  pageCount={pageCount}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            </div>
          </div>

          {/* Edit Modal using GlassmorphicModal */}
          <GlassmorphicModal
            isOpen={showEditModal}
            onClose={closeModal}
            title="Edit News Item"
            size="md"
            footer={editModalFooter}
          >
            {currentNewsItem && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    maxLength={100}
                    value={currentNewsItem.title || ''}
                    onChange={(e) => setCurrentNewsItem({ ...currentNewsItem, title: e.target.value })}
                    className={`w-full rounded-md border ${formErrors.title ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {formErrors.title && <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={currentNewsItem.newscontent || ''}
                    onChange={(e) => setCurrentNewsItem({ ...currentNewsItem, newscontent: e.target.value })}
                    className={`w-full rounded-md border ${formErrors.newscontent ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    rows="4"
                  />
                  {formErrors.newscontent && <p className="text-red-500 text-xs mt-1">{formErrors.newscontent}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date Published <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={currentNewsItem.datepublished ? new Date(currentNewsItem.datepublished).toISOString().split('T')[0] : ''}
                    onChange={(e) => setCurrentNewsItem({ ...currentNewsItem, datepublished: e.target.value })}
                    className={`w-full rounded-md border ${formErrors.datepublished ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {formErrors.datepublished && <p className="text-red-500 text-xs mt-1">{formErrors.datepublished}</p>}
                </div>
                <div className="text-sm text-red-500 mt-4">* indicates required field</div>
              </div>
            )}
          </GlassmorphicModal>

          {/* Delete Modal using GlassmorphicModal */}
          <GlassmorphicModal
            isOpen={showDeleteModal}
            onClose={closeModal}
            title="Confirm Delete"
            size="sm"
            footer={deleteModalFooter}
          >
            <p className="text-gray-700">
              Are you sure you want to delete this news item? This action cannot be undone.
            </p>
          </GlassmorphicModal>
        </div>
      )}
    </div>
  );
};

export default Newpublishing;